import type { BankLinkFailure } from '../collectors/enablebanking/link'

/**
 * What the bank callback says, and where it says it.
 *
 * Kept apart from the route that writes it so the page that has to read it back
 * can import the same declarations: `banks.ts` reaches the database and the
 * filesystem, and a page importing it would drag both into the browser build.
 * The two halves went a long time answering and discarding across a channel
 * neither of them shared.
 */

/**
 * Where in the app the person lands once the bank has sent them back. A
 * relative path, so nothing a bank puts in the query string can turn this into
 * a redirect somewhere else.
 */
export const BANK_CONSENT_PATH = '/sources'

/** The consent waiting to be attributed, and why there is none. */
export const CONSENT_PARAM = 'consent'
export const FAILURE_PARAM = 'bank_error'

/** What is reported when the failure was none of the ones the link can name. */
export const UNEXPECTED_FAILURE = 'provider_error'

/**
 * Every answer `FAILURE_PARAM` can carry. A union rather than a string, so the
 * page that turns one into a sentence can be exhaustive over it and a failure
 * added to the link stops the build until somebody has said what it means.
 */
export type BankCallbackFailure = BankLinkFailure | typeof UNEXPECTED_FAILURE

/**
 * Every failure the callback can report, and what the reader does about it.
 *
 * Typed as a total record so that a failure added to `BankCallbackFailure`
 * fails the build here rather than reaching somebody's screen as a bare code.
 */
export const FAILURE_MESSAGES: Record<BankCallbackFailure, string> = {
  /* A code is spent the moment it is exchanged, so a reloaded callback tab or a
     second press on the same link lands here having approved nothing — which is
     nothing like the bank turning you down, and must not read as it. */
  unknown_authorization:
    'That link back from your bank had already been used, so nothing new was approved. Your bank has not refused you — start the connection again below.',
  authorization_expired:
    'Too long passed between starting at your bank and coming back, so the attempt was abandoned. Nothing was connected. Start it again below and finish the approval in one go.',
  bank_refused:
    'Your bank sent you back without an approval, which is what happens when MitID is cancelled or the bank declines. Nothing was connected. You can try again below.',
  not_configured:
    'This dashboard has no Enable Banking application set up, so no bank can be connected. Nothing you did caused this, and trying again will not help until whoever runs the dashboard has finished setting it up.',
  no_redirect_url:
    'This dashboard has no valid address for your bank to return you to, so the approval had nowhere to land. It has to be corrected where the dashboard is configured — trying again will not help.',
  unknown_bank:
    'The bank this attempt was for is no longer on the list your dashboard is offered. Choose it again below; if it is not there, it cannot be connected at the moment.',
  unsupported_psu_type:
    'That bank does not offer the kind of approval this attempt asked for. Start again below and choose the other of personal or business.',
  unknown_consent:
    'The approval this was for is no longer waiting to be attributed — it has either been saved already or replaced by a newer one. Check the accounts below before starting again.',
  provider_error:
    'Something went wrong between this dashboard and Enable Banking, and nothing was connected. Try again; if it keeps happening, the dashboard needs looking at rather than your bank.',
}

/** Anything short and word-shaped enough to be worth quoting back to a reader. */
const QUOTABLE = /^[a-z0-9_-]{1,40}$/i

/**
 * A code this build has never heard of still has to say something true: that
 * nothing was connected, and what to do. A deployment newer than the page it is
 * serving is exactly when a blank would be read as success.
 */
function unknownFailure(failure: string): string {
  const named = QUOTABLE.test(failure) ? `“${failure}”` : 'something this page cannot print'
  return `Your bank sent you back with a result this page does not recognise: ${named}. Nothing has been connected — try again below, and quote that if it keeps happening.`
}

/**
 * The one place a failure becomes words. The page a bank returns to and the
 * source card a household reads afterwards are the same sentence, because they
 * are the same event seen at two moments.
 */
export function bankFailureMessage(failure: string): string {
  return Object.hasOwn(FAILURE_MESSAGES, failure)
    ? FAILURE_MESSAGES[failure as BankCallbackFailure]
    : unknownFailure(failure)
}
